import { BillingConfig, BillingPermission, NumberSeries } from '../types/billing'
import { Role } from '../types'

export const BMED_LOGO = 'https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png'

export const permissionLabels: Record<BillingPermission, string> = {
  createDocuments: 'Create invoices, quotations and orders',
  issueInvoices: 'Issue invoices',
  recordPayments: 'Record payments on invoices and deposits',
  directSales: 'Make cash sales / direct receipts',
  editPrices: 'Override catalogue prices on documents',
  applyDiscounts: 'Apply discounts (up to the approval limit)',
  approveDiscounts: 'Approve discounts above the limit',
  issueCredits: 'Issue credit notes and apply account credit',
  refunds: 'Authorise refunds',
  voids: 'Void invoices and sales, reverse payments',
  viewReports: 'View billing reports',
  exportData: 'Export reports, items and backups',
  manageCatalogue: 'Add and edit items, services and prices',
  manageTaxes: 'Create and change tax codes',
  manageSettings: 'Change billing settings and run migration',
}

export const seriesLabels: Record<NumberSeries, string> = {
  invoice: 'Invoices',
  saleReceipt: 'Direct-sale receipts',
  receipt: 'Payment receipts',
  quotation: 'Quotations',
  order: 'Orders',
  creditNote: 'Credit notes',
  refund: 'Refunds',
}

export const billingRoles: Role[] = ['Administrator', 'Reception']

export const defaultBillingConfig: BillingConfig = {
  business: {
    name: 'BMED MEDICAL CENTRE',
    tagline: 'Caring · Healing · Leading',
    addressLine1: 'Plot No. 1884/10, Along Avic Road, Lilayi Estate',
    addressLine2: 'Kafue, Lusaka - Zambia',
    phone: '+260975202011',
    email: 'bmedhospital@gmail.com',
    tpin: '2000869447',
    registrationLabel: '',
    registrationNumber: '',
    logoUrl: BMED_LOGO,
    bankDetails: '',
    documentHeader: '',
  },
  regional: {
    currency: 'ZMW',
    currencySymbol: 'K',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: '24h',
    timezone: 'Africa/Lusaka',
    weekStart: 'Monday',
    financialYearStartMonth: 1,
    defaultReport: 'daily',
  },
  numbering: {
    invoice: { prefix: 'INV-', padding: 6 },
    saleReceipt: { prefix: 'CS-', padding: 6 },
    receipt: { prefix: 'RCT-', padding: 6 },
    quotation: { prefix: 'QUO-', padding: 6 },
    order: { prefix: 'ORD-', padding: 6 },
    creditNote: { prefix: 'CN-', padding: 6 },
    refund: { prefix: 'RF-', padding: 6 },
  },
  invoiceDefaults: {
    termsDays: 0,
    paymentTerms: 'Due on receipt',
    notes: '',
    footer: 'Thank you for choosing BMED Medical Centre.',
    quotationValidityDays: 30,
  },
  appearance: {
    paperSize: 'A4',
    marginMm: 14,
    font: 'Open Sans',
    accentColor: '#221587',
    logoPlacement: 'left',
    logoSizeMm: 17,
    showPatientNumber: true,
    showEncounter: true,
    showStaff: true,
    showDiscountColumn: true,
    showTaxColumn: true,
    showTaxBreakdown: true,
    showSignatures: true,
  },
  taxes: {
    pricesIncludeTax: false,
    defaultTaxId: 'exempt',
    taxLabel: 'Tax',
    reportBasis: 'accrual',
    rates: [
      { id: 'exempt', code: 'E', name: 'Exempt', rate: 0, classification: 'Exempt', effectiveFrom: '2000-01-01', active: true },
      { id: 'zero', code: 'Z', name: 'Zero-rated', rate: 0, classification: 'Zero-rated', effectiveFrom: '2000-01-01', active: false },
      { id: 'vat-16', code: 'S', name: 'VAT (verify rate before use)', rate: 16, classification: 'Standard', effectiveFrom: '2000-01-01', active: false },
    ],
  },
  catalogue: {
    categories: ['Consultation', 'Laboratory', 'Radiology', 'Procedure', 'Pharmacy', 'Ward & Admission', 'Maternity', 'Other'],
    departments: ['Clinical', 'Laboratory', 'Radiology', 'Pharmacy', 'Maternity', 'Nursing', 'Reception', 'Other'],
    units: ['Each', 'Visit', 'Test', 'Scan', 'Tablet', 'Bottle', 'Pack', 'Day', 'Session'],
  },
  discounts: { maxPctWithoutApproval: 10 },
  sales: { directSalesEnabled: true, walkInEnabled: true, splitTenders: true, requirePhone: false, requireCustomerName: true },
  paymentMethods: [
    { id: 'cash', name: 'Cash', enabled: true, requiresReference: false },
    { id: 'mobile-money', name: 'Mobile Money', enabled: true, requiresReference: true, accountDetails: '' },
    { id: 'bank-transfer', name: 'Bank Transfer', enabled: true, requiresReference: true, accountDetails: '' },
    { id: 'card', name: 'Card', enabled: true, requiresReference: true },
    { id: 'insurance', name: 'Insurance settlement', enabled: true, requiresReference: true },
    { id: 'corporate', name: 'Corporate account', enabled: true, requiresReference: true },
    { id: 'cheque', name: 'Cheque', enabled: false, requiresReference: true },
  ],
  receipts: { footer: 'Received with thanks.', showInvoiceBalance: true, overpayment: 'credit' },
  statements: { ageingDays: [30, 60, 90], message: 'Please settle overdue balances at the cashier.', includePaid: true },
  email: {
    provider: 'none',
    fromName: 'BMED Medical Centre Billing',
    fromAddress: 'bmedhospital@gmail.com',
    invoiceSubject: 'Invoice {number} from BMED Medical Centre',
    invoiceBody: 'Dear {name},\n\nPlease find attached invoice {number} for {total}.\n\nKind regards,\nBMED Medical Centre',
    remindersEnabled: false,
    reminderDays: [7, 14, 30],
  },
  recurring: { defaultFrequency: 'Monthly', autoIssue: false },
  permissions: {
    createDocuments: ['Administrator', 'Reception'],
    issueInvoices: ['Administrator', 'Reception'],
    recordPayments: ['Administrator', 'Reception'],
    directSales: ['Administrator', 'Reception'],
    editPrices: ['Administrator'],
    applyDiscounts: ['Administrator'],
    approveDiscounts: ['Administrator'],
    issueCredits: ['Administrator'],
    refunds: ['Administrator'],
    voids: ['Administrator'],
    viewReports: ['Administrator', 'Reception'],
    exportData: ['Administrator'],
    manageCatalogue: ['Administrator', 'Reception'],
    manageTaxes: ['Administrator'],
    manageSettings: ['Administrator'],
  },
  migration: { status: 'not-started' },
}

/** Kept for older screens; the editable list lives in Billing Settings → Items & pricing. */
export const catalogueCategories = defaultBillingConfig.catalogue.categories
